'use client';

import React, { memo, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import posthog from 'posthog-js';

import { Metrics, SNIPPET_TYPE } from '@repo/types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@repo/ui/components/ui/accordion';
import { Button } from '@repo/ui/components/ui/button';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/ui/tooltip';
import { cn } from '@repo/ui/lib/utils';

import { AnalysisTooltipContent } from '@/components/article-analysis/analysis-container';

import { BiasChart } from './bias-chart';
import { CredibleChart } from './credible-chart';
import { ScoresChart } from './scores-chart';

interface ChartsProps {
  metrics?: Metrics;
  currTab: SNIPPET_TYPE;
  setCurrTab: (tab: SNIPPET_TYPE) => void;
  /** Article slug (analytics wire id), threaded to ScoresChart. */
  articleId?: string;
}

const TooltipText = memo<{ title: string; content?: string }>(
  ({ title, content }) => (
    <div className="flex flex-col">
      <p className="text-[10px] font-bold leading-3.25 text-neutral-2 @cred-md:text-[12px] @cred-md:leading-4 @cred-xl:text-[14px] @cred-xl:leading-4.5">
        {title}:
      </p>
      <p className="text-[10px] font-normal leading-3.25 text-neutral-2 @cred-md:text-[12px] @cred-md:leading-4 @cred-xl:text-[14px] @cred-xl:leading-4.5">
        {content}
      </p>
    </div>
  ),
);

TooltipText.displayName = 'TooltipText';

const ChartWithTooltip = memo<{
  children: React.ReactNode;
  title: string;
  content?: string;
}>(({ children, title, content }) => (
  <TooltipProvider>
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <div className="flex w-full justify-start">{children}</div>
      </TooltipTrigger>
      <AnalysisTooltipContent className="z-200 max-w-72 rounded-md bg-light-blue px-3 py-2 shadow-[1px_2px_4px_rgba(30,30,30,0.4)] @cred-md:max-w-80 @cred-xl:max-w-110">
        <TooltipText title={title} content={content} />
      </AnalysisTooltipContent>
    </Tooltip>
  </TooltipProvider>
));

ChartWithTooltip.displayName = 'ChartWithTooltip';

const DEFAULT_ACCORDION_DATA = {
  type: '',
  title: '',
  content: '',
} as const;

export const Charts: React.FC<ChartsProps> = ({
  metrics,
  currTab,
  setCurrTab,
  articleId,
}) => {
  const [accordionValue, setAccordionValue] = useState('');
  const [accordionData, setAccordionData] = useState<{
    type: 'credibility' | 'bias' | '';
    title?: string;
    label?: string;
    content: string;
  }>(DEFAULT_ACCORDION_DATA);

  const credibilityChart = useMemo(
    () => <CredibleChart credibilityScore={metrics?.credibilityScore} />,
    [metrics?.credibilityScore],
  );

  const biasChart = useMemo(
    () => <BiasChart biasScore={metrics?.biasScore} />,
    [metrics?.biasScore],
  );

  const scoresChart = useMemo(
    () => (
      <ScoresChart
        currTab={currTab}
        setCurrTab={setCurrTab}
        claimedFactsScores={metrics?.claimedFactsScores}
        opinionStatementsScores={metrics?.opinionStatementsScores}
        dubiousClaimsScores={metrics?.dubiousClaimsScores}
        articleId={articleId}
      />
    ),
    [
      currTab,
      setCurrTab,
      metrics?.claimedFactsScores,
      metrics?.opinionStatementsScores,
      metrics?.dubiousClaimsScores,
      articleId,
    ],
  );

  const handleAccordionClick = (type: 'credibility' | 'bias') => {
    const biasLabel = metrics?.biasLabel || 'Bias';

    // Determine the resulting state BEFORE we mutate it so we can fire
    // the right analytics action. There are 4 possible transitions:
    //   1. closed -> open (any type) = EXPAND
    //   2. open with same type = COLLAPSE
    //   3. open with different type = SWITCH (an expand of the new type)
    //   4. nothing-to-nothing impossible
    // We fire `reasoning_expanded` for case 1 and case 3 (the user is
    // newly seeing reasoning content), and `reasoning_collapsed` for
    // case 2 (the user is hiding it). Both are analytically interesting:
    // expanded = engagement signal, collapsed = "I read enough" signal.
    const isCurrentlyOpen = accordionValue === 'charts-accordion';
    const isSameType = accordionData.type === type;
    const isCollapsing = isCurrentlyOpen && isSameType;

    if (isCollapsing) {
      posthog.capture('reasoning_collapsed', {
        content_type: 'article',
        reasoning_type:
          type === 'credibility' ? 'credibility_score' : 'bias_label',
      });
    } else {
      posthog.capture('reasoning_expanded', {
        content_type: 'article',
        reasoning_type:
          type === 'credibility' ? 'credibility_score' : 'bias_label',
        // Whether this was a fresh expand (closed -> open) or a switch
        // (open with one type -> open with the other). The analyst
        // wants both the "fresh-expand" rate AND the "switch" rate as
        // separate engagement signals.
        transition: isCurrentlyOpen ? 'switch' : 'fresh_expand',
      });
    }

    if (accordionValue === 'charts-accordion') {
      if (
        (type === 'credibility' && accordionData.type === 'credibility') ||
        (type === 'bias' && accordionData.type === 'bias')
      ) {
        setAccordionValue('');
        setTimeout(() => {
          setAccordionData(DEFAULT_ACCORDION_DATA);
        }, 300);
      } else if (type === 'credibility' && accordionData.type === 'bias') {
        setAccordionData({
          type,
          content: metrics?.credibilityReasoning || '',
        });
      } else if (type === 'bias' && accordionData.type === 'credibility') {
        setAccordionData({
          type,
          label: biasLabel,
          content: metrics?.biasLabelReasoning || '',
        });
      }
    } else {
      setAccordionValue('charts-accordion');

      if (type === 'credibility') {
        setAccordionData({
          type,
          content: metrics?.credibilityReasoning || '',
        });
      } else {
        setAccordionData({
          type,
          label: biasLabel,
          content: metrics?.biasLabelReasoning || '',
        });
      }
    }
  };

  return (
    <Accordion
      type="single"
      value={accordionValue}
      onValueChange={setAccordionValue}
      collapsible
      className="w-full"
    >
      <AccordionItem
        value="charts-accordion"
        className="w-full flex flex-col items-center border-none"
      >
        <div className="grid w-full grid-cols-3 gap-1">
          <div
            onClick={() => handleAccordionClick('credibility')}
            className="flex flex-col gap-2 cursor-pointer"
          >
            <ChartWithTooltip
              title="Reason"
              content={metrics?.credibilityReasoning}
            >
              {credibilityChart}
            </ChartWithTooltip>
            <Button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAccordionClick('credibility');
              }}
              data-active={accordionData.type === 'credibility'}
              className="h-auto pl-2.5 pr-1.5 py-0.5 @cred-lg:py-0.75 @cred-xl:py-1 bg-transparent hover:bg-white rounded-full cursor-pointer flex items-center justify-center gap-0.5 text-white hover:text-black transition-colors border-white border no-underline hover:no-underline text-[12.5px] @cred-md:text-[13.5px] @cred-xl:text-[15.5px] data-[active=true]:text-black data-[active=true]:bg-white"
            >
              {`Why ${Math.round(metrics?.credibilityScore || 0)}%?`}
              <ChevronDown
                className={cn(
                  'cursor-pointer size-4 transition-transform @cred-md:size-4.5 @cred-lg:size-5 @cred-xl:size-6',
                  accordionData.type === 'credibility' && 'rotate-180',
                )}
              />
            </Button>
          </div>
          <div
            className="flex flex-col gap-2 cursor-pointer"
            onClick={() => handleAccordionClick('bias')}
          >
            <ChartWithTooltip
              title={metrics?.biasLabel || 'Bias'}
              content={metrics?.biasLabelReasoning}
            >
              {biasChart}
            </ChartWithTooltip>
            <Button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAccordionClick('bias');
              }}
              data-active={accordionData.type === 'bias'}
              className="h-auto pl-2.5 pr-1.5 py-0.5 @cred-lg:py-0.75 @cred-xl:py-1 bg-transparent hover:bg-white rounded-full cursor-pointer flex items-center justify-center gap-0.5 text-white hover:text-black transition-colors border-white border no-underline hover:no-underline text-[12.5px] @cred-md:text-[13.5px] @cred-xl:text-[15.5px] data-[active=true]:text-black data-[active=true]:bg-white"
            >
              {`Why ${Math.round(metrics?.biasScore || 0)}%?`}
              <ChevronDown
                className={cn(
                  'cursor-pointer size-4 transition-transform @cred-md:size-4.5 @cred-lg:size-5 @cred-xl:size-6',
                  accordionData.type === 'bias' && 'rotate-180',
                )}
              />
            </Button>
          </div>

          <div className="flex w-full justify-start">{scoresChart}</div>
        </div>

        <AccordionContent className="w-full px-0 pb-0 pt-2">
          <div className="w-full bg-[#F0F6FF] border-l-[#2979FF] border-l-2 px-2.5 py-1.5 rounded-b-2xl font-roboto">
            <p className="text-[11px] font-normal leading-3.25 text-neutral-2 @cred-md:text-[13px] @cred-md:leading-4 @cred-lg:text-sm @cred-lg:leading-[16.5px] @cred-xl:text-base @cred-xl:leading-[21.7px]">
              {accordionData.title && (
                <b className="font-bold">{accordionData.title} </b>
              )}
              {accordionData.label && (
                <b className="font-bold">{accordionData.label}: </b>
              )}
              {accordionData.content}
            </p>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
